import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { RootState, AppDispatch } from '../../redux/store';
import { logout } from '../../redux/auth/authSlice';
import Sidebar from '../../components/sidebar/Sidebar';
import { CreditCard, ExternalLink, Settings } from 'lucide-react';
import { ResponsiveAppShell, TaskyTopBar } from '../../components/tasky';
import LoginMethodCard from '../../components/account/LoginMethodCard';
import McpTokenCard from '../../components/account/McpTokenCard';
import AdReportSettingsPanel from '../../components/ad-dashboard/AdReportSettingsPanel';

/* 旧 styled-components 定義をクラス文字列定数として保持。
 * font-size は line-height 未指定だったため arbitrary value を使う。
 * メディアクエリは元の px 境界を保つため max-[Npx]: / lg: を使い分ける。 */
const PAGE_WRAPPER = 'flex min-h-[calc(100dvh-56px)] bg-warm-bg lg:h-screen lg:min-h-0';

const MAIN_CONTENT = 'flex-1 flex flex-col';

const CONTENT_WRAPPER = 'flex-1 overflow-y-auto overflow-x-hidden p-6 max-[640px]:p-4';

const CONTAINER = 'max-w-[800px] mx-auto p-6 max-[640px]:p-0';

const CARD =
  'bg-white border border-warm-border rounded-tasky-md shadow-[0_1px_3px_rgba(29,25,39,0.04)]';

const SECTION_TITLE = 'text-[16px] font-semibold text-dark m-0 mb-4';

const LINK_GRID =
  'grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-3 max-[640px]:grid-cols-[1fr]';

const BILLING_LINK =
  'inline-flex items-center justify-center gap-2 min-h-[42px] px-3.5 py-2.5 ' +
  'border border-[#DAD6CC] rounded-tasky-sm text-dark bg-white text-[14px] ' +
  'font-semibold no-underline hover:bg-warm-bg hover:border-[#C9C2B3] ' +
  'max-[640px]:w-full';

const CARD_CONTENT = 'p-6 max-[640px]:p-4';

const SETTING_ITEM = 'py-4 border-b border-b-warm-border last-of-type:border-b-0';

const LABEL = 'block text-[14px] font-medium text-gray-500 mb-2'; /* #6B7280 */

const VALUE = 'text-[16px] text-dark m-0 [overflow-wrap:anywhere]';

const NOTICE = 'mt-6 text-[14px] text-gray-500 italic'; /* #6B7280 */

const AccountSettings: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector((state: RootState) => state.auth.user);
  const notificationSectionRef = useRef<HTMLElement>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const stripeBillingUrl = process.env.REACT_APP_STRIPE_BILLING_PORTAL_URL
    || 'https://billing.stripe.com/p/login/28E5kF0Kgawx9i93pOcs800';

  useEffect(() => {
    if (location.hash === '#notification-integrations') {
      notificationSectionRef.current?.scrollIntoView({ block: 'start' });
    }
  }, [location.hash, user?.organization_id]);

  // ログアウト処理
  const handleLogout = async () => {
    await dispatch(logout());
    navigate('/login');
  };

  const sidebar = (
    <Sidebar
      onLogout={handleLogout}
      onToggleCollapse={setSidebarCollapsed}
      collapsed={sidebarCollapsed}
    />
  );
  const mobileSidebar = <Sidebar onLogout={handleLogout} />;

  return (
    <ResponsiveAppShell sidebar={sidebar} mobileSidebar={mobileSidebar} topbarTitle="アカウント設定">
    <div className={PAGE_WRAPPER}>
      <div className={MAIN_CONTENT}>
        <TaskyTopBar
          title="アカウント設定"
          icon={<Settings className="w-[22px] h-[22px]" />}
          sidebarCollapsed={sidebarCollapsed}
          onExpandSidebar={() => setSidebarCollapsed(false)}
        />

        {/* メインコンテンツ */}
        <div className={CONTENT_WRAPPER}>
          <div className={CONTAINER}>
            <div className={CARD} data-tutorial="account-settings-profile">
              <div className={CARD_CONTENT}>
                <div className={SETTING_ITEM}>
                  <label className={LABEL}>名前</label>
                  <p className={VALUE}>{(user?.name && user.name !== '未設定' && user.name !== '名称未設定')
                    ? user.name
                    : user?.email ? user.email.split('@')[0] : '未設定'}</p>
                </div>

                <div className={SETTING_ITEM}>
                  <label className={LABEL}>メールアドレス</label>
                  <p className={VALUE}>{user?.email || '未設定'}</p>
                </div>

                <div className={SETTING_ITEM}>
                  <label className={LABEL}>ロール</label>
                  <p className={VALUE}>
                    {user?.role === 'leader' ? 'リーダー' : user?.role === 'member' ? 'メンバー' : user?.role || '未設定'}
                  </p>
                </div>

                {user?.organization_id && (
                  <div className={SETTING_ITEM}>
                    <label className={LABEL}>組織ID</label>
                    <p className={VALUE}>{user.organization_id}</p>
                  </div>
                )}

                {user?.organization?.plan && (
                  <div className={SETTING_ITEM}>
                    <label className={LABEL}>サブスクリプションプラン</label>
                    <p className={VALUE}>{user.organization.plan}</p>
                  </div>
                )}

                <p className={NOTICE}>
                  ※ 名前・メールアドレスの編集機能は今後追加予定です
                </p>
              </div>
            </div>

            <LoginMethodCard />

            {user?.organization_id && (
              <section ref={notificationSectionRef} id="notification-integrations" aria-label="通知連携" className="mt-6 scroll-mt-6">
                <AdReportSettingsPanel canEdit={user.role === 'leader'} />
              </section>
            )}

            <McpTokenCard />

            <div className={CARD} style={{ marginTop: 24 }} data-tutorial="account-settings-billing">
              <div className={CARD_CONTENT}>
                <h2 className={SECTION_TITLE}>サブスクリプション・支払い</h2>
                <div className={LINK_GRID}>
                  <a className={BILLING_LINK} href="/plans">
                    <CreditCard className="w-4 h-4" />
                    <span>サブスクを変更</span>
                  </a>
                  <a className={BILLING_LINK} href={stripeBillingUrl} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="w-4 h-4" />
                    <span>支払い内容の確認・変更</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </ResponsiveAppShell>
  );
};

export default AccountSettings;
